import { IQueryResultSettings, ProductRefiner } from '@msdyn365-commerce/retail-proxy';
/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/
import * as Msdyn365 from '@msdyn365-commerce/core';
import { IProductRefinerHierarchy } from '@msdyn365-commerce/commerce-entities';
import { createObservableDataAction, IAction, IActionContext, IActionInput, ICreateActionContext } from '@msdyn365-commerce/core';

import { getProductSearchRefinersAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

/**
 * Refiners-by-Collection Input action.
 */
export class AllRefinersInput implements IActionInput {
    public getCacheKey = () => `ProductRefiner`;

    public getCacheObjectType = () => 'ProductRefiner';

    public dataCacheType = (): Msdyn365.CacheType => 'application';
}

/**
 * Create input method which creates an ActionInput for fetching list page refiners.
 * @param args
 */
const createInput = (args: ICreateActionContext): IActionInput => {
    return new AllRefinersInput();
};

/**
 * Action method which fetches refiners for the given list page.
 * @param input
 * @param ctx
 */
async function getAllRefinersAction(input: AllRefinersInput, ctx: IActionContext): Promise<IProductRefinerHierarchy[]> {
    const queryResultSettings: IQueryResultSettings = {
        count: true,
        Sorting: {
            Columns: [
                {
                    ColumnName: 'name',
                    IsDescending: true
                }
            ]
        }
    };
    const searchCriteria = {
        SearchCondition: '*',
        Context: {
            ChannelId: ctx.requestContext.apiSettings.channelId,
            CatalogId: ctx.requestContext.apiSettings.catalogId
        }
    };
    return getProductSearchRefinersAsync({ callerContext: ctx, queryResultSettings }, searchCriteria).then(
        (productRefiners: ProductRefiner[]) => {
            if (!productRefiners) {
                ctx.trace('[getRefinersByCriteriaAction] returned no refiners');
                return <IProductRefinerHierarchy[]>[];
            }

            const result = (productRefiners || []).map((productRefiner: ProductRefiner) => <IProductRefinerHierarchy>productRefiner);

            if (result.length === 0) {
                ctx.trace('[getRefinersByCriteriaAction] returned no IProductRefinerHierarchy objects');
                return <IProductRefinerHierarchy[]>[];
            }

            return result;
        }
    );
}

export const actionDataAction = createObservableDataAction({
    id: 'get-all-refiners-for-collection',
    action: <IAction<IProductRefinerHierarchy[]>>getAllRefinersAction,
    input: createInput
});

export default actionDataAction;
